import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Terminal,
  Clock,
  Gauge,
  Play,
  Loader2,
  ChevronLeft,
  CheckCircle,
  FlaskConical,
} from "lucide-react";

const DIFFICULTY_LABELS = {
  easy: { label: "Facile", className: "text-green-500 border-green-500/40" },
  medium: { label: "Intermédiaire", className: "text-yellow-500 border-yellow-500/40" },
  hard: { label: "Avancé", className: "text-red-500 border-red-500/40" },
};

const LabDetail = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lab, setLab] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLab = async () => {
      try {
        const response = await axios.get(`${API}/labs/${labId}`);
        setLab(response.data);
      } catch (error) {
        setLab(null);
      } finally {
        setLoading(false);
      }
    };
    fetchLab();
  }, [labId]);

  const startLab = () => {
    if (!user) {
      navigate(`/auth?redirect=/labs/${labId}`);
      return;
    }
    navigate(`/labs/${lab.lab_id}/run`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <h1 className="font-heading font-bold text-2xl mb-2">Lab introuvable</h1>
          <Link to="/labs"><Button variant="outline">Retour au catalogue</Button></Link>
        </div>
      </div>
    );
  }

  const diff = DIFFICULTY_LABELS[lab.difficulty] || DIFFICULTY_LABELS.easy;

  return (
    <div className="min-h-screen bg-background" data-testid="lab-detail">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/labs" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ChevronLeft className="w-4 h-4" /> Catalogue des labos
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenu principal */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="uppercase">{lab.environment}</Badge>
              <Badge variant="outline" className={diff.className}>{diff.label}</Badge>
              {lab.tags?.map((t) => (
                <Badge key={t} variant="outline">{t}</Badge>
              ))}
            </div>

            <h1 className="font-heading font-bold text-3xl">{lab.title}</h1>
            <p className="text-muted-foreground text-lg">{lab.description}</p>

            {lab.thumbnail && (
              <img src={lab.thumbnail} alt={lab.title} className="w-full rounded-xl border border-border" />
            )}

            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-primary" /> Instructions
              </h2>
              <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-muted-foreground">
                {lab.instructions}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-6 space-y-4 lg:sticky lg:top-24">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2"><Clock className="w-4 h-4" /> Durée estimée</span>
                <span className="font-medium">{lab.estimated_time_minutes} min</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2"><Gauge className="w-4 h-4" /> Difficulté</span>
                <span className="font-medium">{diff.label}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-2"><Terminal className="w-4 h-4" /> Environnement</span>
                <span className="font-medium capitalize">{lab.environment}</span>
              </div>

              <Button onClick={startLab} className="w-full" size="lg" data-testid="start-lab-btn">
                <Play className="w-4 h-4 mr-2" /> Démarrer le lab
              </Button>

              <ul className="text-sm text-muted-foreground space-y-2 pt-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Terminal interactif</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Étapes guidées</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-primary" /> Validation automatique</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LabDetail;
